import React, { useState } from 'react';
import { Modal, Card, Tabs } from 'antd';
import { Close } from '@mui/icons-material';
import { FormControlLabel, Switch } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import { find, get } from 'lodash';
import styled from 'styled-components';
import COLOR from 'utils/constants/color';
import { getStandardAbbr, filterStateStandardByEnabledStates, formatStateNameForDisplay, isIllinoisOrCommonCoreState, normalizeStateForComparison, getStateNameFromCode, getStateCodeFromName, statesMatch, getStateNameDisplayForCode } from 'utils/lib/standardHelper';

const ModalContent = styled.div`
    padding: 8px 0;
    max-height: 70vh;
    overflow-y: auto;
`;

const StandardCard = styled(Card)`
    margin-bottom: 16px;
    background-color: #f0f7ff;
    border: 1px solid #91d5ff;
`;

const Description = styled.div`
    font-size: 13px;
    color: #374151;
    line-height: 1.5;
    white-space: pre-wrap;
    margin-top: 12px;
`;

const StandardCode = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #1890ff;
    margin-bottom: 8px;
`;

const StateCodeLabel = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 8px;
`;

const TabContent = styled.div`
    padding: 16px 0;
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const HeaderTitle = styled.span`
    flex: 1;
`;

const HeaderControls = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-right: 40px;
`;

const StyledFormControlLabel = muiStyled(FormControlLabel)({
    margin: 0,
    '& .MuiFormControlLabel-label': {
        fontSize: '12px',
        color: COLOR.black200,
        fontWeight: 500,
    },
});

const StyledSwitch = muiStyled(Switch)({
    '&.MuiSwitch-sizeSmall': {
        width: '40px',
        height: '25px',
    },
    '& .Mui-checked': {
        transform: 'translateX(16px) !important',
    },
});

const StandardsModal = ({ 
    open, 
    onClose, 
    standardAbbrs, 
    stateStandard,
    standards, 
    enabledStateCodes = [],
    userRole = null
}) => {
    const isAdmin = userRole === 'admin' || userRole === 'owe_admin';
    const shouldFilterByEnabledStates = !isAdmin && enabledStateCodes.length > 0;
    const [activeStateCode, setActiveStateCode] = useState('');
    const [showEnglishDescription, setShowEnglishDescription] = useState(false);

    const formatGradeDisplay = (grade, stateCode) => {
        if (!grade) return grade;
        
        if (!isIllinoisOrCommonCoreState(stateCode)) {
            return grade;
        }
        
        const gradeStr = String(grade).trim();
        let normalizedGrade = gradeStr.replace(/\s*(th|st|nd|rd|grade)\s*/gi, '').trim();
        normalizedGrade = normalizedGrade.replace(/\//g, '-');
        
        const gradeNumbers = normalizedGrade.split('-').map(g => g.trim()).filter(g => g);
        
        if (gradeNumbers.length === 1) {
            const num = gradeNumbers[0];
            if (num === '9' || num === '10') {
                return '9/10';
            }
            if (num === '11' || num === '12') {
                return '11/12';
            }
        } else if (gradeNumbers.length === 2) {
            const [first, second] = gradeNumbers;
            if ((first === '9' && second === '10') || (first === '10' && second === '9')) {
                return '9/10';
            }
            if ((first === '11' && second === '12') || (first === '12' && second === '11')) {
                return '11/12';
            }
            return `${first}/${second}`;
        }
        
        return grade;
    };

    const getStandardData = () => {
        if (stateStandard && Array.isArray(stateStandard) && stateStandard.length > 0) {
            const filteredStateStandard = shouldFilterByEnabledStates 
                ? filterStateStandardByEnabledStates(stateStandard, enabledStateCodes)
                : stateStandard;
            const allAbbrs = new Set();
            filteredStateStandard.forEach((stateStd) => {
                if (stateStd && Array.isArray(stateStd.standard)) {
                    stateStd.standard.forEach(abbr => allAbbrs.add(abbr));
                }
            });
            
            return Array.from(allAbbrs).map((standardAbbr) => {
                const selectedStandard = find(standards, ["abbr", standardAbbr]);
                if (!selectedStandard) {
                    return {
                        standard: { abbr: standardAbbr, description: '', description_english: '' },
                        description: '',
                        descriptionEnglish: '',
                        state: ''
                    };
                }
                
                return {
                    standard: selectedStandard,
                    description: get(selectedStandard, 'description', ''),
                    descriptionEnglish: get(selectedStandard, 'description_english', ''),
                    state: get(selectedStandard, 'state', '')
                };
            });
        }
        
        if (!Array.isArray(standardAbbrs) || standardAbbrs.length === 0) {
            return [];
        }
        
        return standardAbbrs.map((standardAbbr) => {
            const selectedStandard = find(standards, ["abbr", standardAbbr]);
            if (!selectedStandard) {
                return {
                    standard: { abbr: standardAbbr, description: '', description_english: '' },
                    description: '',
                    descriptionEnglish: '',
                    state: ''
                };
            }
            
            return {
                standard: selectedStandard,
                description: get(selectedStandard, 'description', ''),
                descriptionEnglish: get(selectedStandard, 'description_english', ''),
                state: get(selectedStandard, 'state', '')
            };
        });
    };

    const standardData = getStandardData();
    
    if ((!stateStandard || !Array.isArray(stateStandard) || stateStandard.length === 0) && 
        (!Array.isArray(standardAbbrs) || standardAbbrs.length === 0)) {
        return null;
    }

    const getAllEnabledStates = () => {
        const enabledStateNames = shouldFilterByEnabledStates 
            ? enabledStateCodes.map(code => normalizeStateForComparison(code)).filter(Boolean)
            : [];
        const allStates = new Map();
        
        if (stateStandard && Array.isArray(stateStandard) && stateStandard.length > 0) {
            const filteredStateStandard = shouldFilterByEnabledStates 
                ? filterStateStandardByEnabledStates(stateStandard, enabledStateCodes)
                : stateStandard;
            
            filteredStateStandard.forEach((stateStd) => {
                if (stateStd && stateStd.standard_name && Array.isArray(stateStd.standard) && stateStd.standard.length > 0) {
                    const stateNameNormalized = normalizeStateForComparison(stateStd.standard_name);
                    const stateNameDisplay = getStateNameFromCode(stateStd.standard_name) || stateStd.standard_name;
                    const stateCodeForDisplay = getStateCodeFromName(stateNameDisplay) || stateStd.standard_name;
                    
                    if (shouldFilterByEnabledStates && enabledStateNames.length === 0) return;
                    
                    const isEnabled = !shouldFilterByEnabledStates || enabledStateNames.some(enabledName => statesMatch(stateStd.standard_name, enabledName));
                    
                    if (isEnabled) {
                        if (!allStates.has(stateNameNormalized)) {
                            allStates.set(stateNameNormalized, {
                                code: stateCodeForDisplay,
                                state: stateNameDisplay
                            });
                        }
                    }
                }
            });
        } else {
            standardData.forEach(data => {
                const stateCode = data.state || '';
                const stateNameNormalized = normalizeStateForComparison(stateCode);
                const stateNameDisplay = getStateNameFromCode(stateCode) || stateCode;
                const stateCodeForDisplay = getStateCodeFromName(stateNameDisplay) || stateCode;
                
                if (shouldFilterByEnabledStates && enabledStateNames.length === 0) return;
                
                const isEnabled = !shouldFilterByEnabledStates || enabledStateNames.some(enabledName => statesMatch(stateCode, enabledName));
                
                if (isEnabled) {
                    if (!allStates.has(stateNameNormalized)) {
                        allStates.set(stateNameNormalized, {
                            code: stateCodeForDisplay,
                            state: stateNameDisplay
                        });
                    }
                }
            });
        }
        
        return Array.from(allStates.values());
    };

    const allEnabledStates = getAllEnabledStates();
    const hasEnabledStates = allEnabledStates.length > 0;
    const selectedStateCode = activeStateCode || (hasEnabledStates ? allEnabledStates[0].code : '');

    const renderStandardsForState = (stateCodeOrName) => {
        if (stateStandard && Array.isArray(stateStandard) && stateStandard.length > 0) {
            const enabledStateStandards = shouldFilterByEnabledStates 
                ? filterStateStandardByEnabledStates(stateStandard, enabledStateCodes)
                : stateStandard;
            let filteredStateStandards = enabledStateStandards;
            
            if (stateCodeOrName) {
                const stateNameToMatch = getStateNameFromCode(stateCodeOrName) || stateCodeOrName;
                filteredStateStandards = enabledStateStandards.filter(stateStd => {
                    const stateStdName = stateStd.standard_name || '';
                    return statesMatch(stateStdName, stateNameToMatch);
                });
            }
            
            const renderedStandards = [];
            
            filteredStateStandards.forEach((stateStd, stateIdx) => {
                if (!stateStd || !Array.isArray(stateStd.standard) || stateStd.standard.length === 0) {
                    return;
                }
                
                const grade = stateStd.grade;
                const standardName = stateStd.standard_name;
                
                stateStd.standard.forEach((abbr, abbrIdx) => {
                    if (!abbr) return;
                    
                    const selectedStandard = find(standards, ["abbr", abbr]);
                    
                    const englishDescription = get(selectedStandard, 'description_english', '');
                    const hasEnglishDescription = englishDescription && englishDescription.trim().length > 0;
                    
                    const displayDescription = selectedStandard 
                        ? (showEnglishDescription 
                            ? (hasEnglishDescription ? englishDescription : 'ENGLISH TRANSLATION IS NOT AVAILABLE.')
                            : (get(selectedStandard, 'description', '') || 'No description available'))
                        : 'No description available';
                    
                    renderedStandards.push(
                        <div key={`${stateIdx}-${abbrIdx}`} style={{ 
                            marginBottom: abbrIdx < stateStd.standard.length - 1 || stateIdx < filteredStateStandards.length - 1 ? '24px' : '0' 
                        }}>
                            <StandardCard>
                                <TabContent>
                                    <StandardCode>{abbr}</StandardCode>
                                    {grade && (
                                        <StateCodeLabel>Grade: {formatGradeDisplay(grade, standardName)}</StateCodeLabel>
                                    )}
                                    {standardName && (
                                        <StateCodeLabel>State: {formatStateNameForDisplay(standardName)}</StateCodeLabel>
                                    )}
                                    <Description>
                                        {displayDescription}
                                    </Description>
                                </TabContent>
                            </StandardCard>
                        </div>
                    );
                });
            });
            
            if (renderedStandards.length === 0 && filteredStateStandards.length > 0) {
                return (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        No standards found for the selected criteria.
                    </div>
                );
            }
            
            return renderedStandards.length > 0 ? renderedStandards : null;
        }
        
        const enabledStateNames = shouldFilterByEnabledStates 
            ? enabledStateCodes.map(code => normalizeStateForComparison(code)).filter(Boolean)
            : [];
        
        if (shouldFilterByEnabledStates && enabledStateNames.length === 0) {
            return [];
        }
        
        const enabledStandardData = shouldFilterByEnabledStates
            ? standardData.filter(data => {
                if (!data.state) return false;
                return enabledStateNames.some(enabledName => statesMatch(data.state, enabledName));
            })
            : standardData;
        
        const filteredStandards = stateCodeOrName 
            ? enabledStandardData.filter(data => {
                const dataState = data.state || '';
                const stateNameToMatch = getStateNameFromCode(stateCodeOrName) || stateCodeOrName;
                return statesMatch(dataState, stateNameToMatch);
            })
            : enabledStandardData;

        return filteredStandards.map((data, idx) => {
            const hasEnglishDescription = data.descriptionEnglish && data.descriptionEnglish.trim().length > 0;
            
            const displayDescription = showEnglishDescription 
                ? (hasEnglishDescription ? data.descriptionEnglish : 'ENGLISH TRANSLATION IS NOT AVAILABLE.')
                : (data.description || 'No description available');
            
            const displayAbbr = data.standard.abbr;

            return (
                <div key={idx} style={{ 
                    marginBottom: idx < filteredStandards.length - 1 ? '24px' : '0' 
                }}>
                    <StandardCard>
                        <TabContent>
                            <StandardCode>{displayAbbr}</StandardCode>
                            {stateCodeOrName && (
                                <StateCodeLabel>State: {formatStateNameForDisplay(stateCodeOrName)}</StateCodeLabel>
                            )}
                            <Description>
                                {displayDescription}
                            </Description>
                        </TabContent>
                    </StandardCard>
                </div>
            );
        });
    };

    const modalTitle = (
        <ModalHeader>
            <HeaderTitle>Standard Information</HeaderTitle>
            <HeaderControls>
                <StyledFormControlLabel
                    control={
                        <StyledSwitch
                            checked={showEnglishDescription}
                            onChange={(e) => {
                                setShowEnglishDescription(e.target.checked);
                            }}
                            name="englishDescription"
                            color="primary"
                            size="small"
                        />
                    }
                    label="English"
                />
            </HeaderControls>
        </ModalHeader>
    );

    if (!hasEnabledStates) {
        return (
            <Modal
                title={modalTitle}
                open={open}
                onCancel={onClose}
                footer={null}
                width={900}
                closeIcon={<Close style={{ fontSize: 18 }} />}
            >
                <ModalContent>
                    {renderStandardsForState(null)}
                </ModalContent>
            </Modal>
        );
    }

    const tabItems = allEnabledStates.map((stateInfo) => {
        const stateNameForComparison = stateInfo.state;
        return {
            key: stateInfo.code,
            label: `${getStateNameDisplayForCode(stateInfo.state)}`,
            children: (
                <div>
                    {renderStandardsForState(stateNameForComparison)}
                </div>
            )
        };
    });

    return (
        <Modal
            title={modalTitle}
            open={open}
            onCancel={onClose}
            footer={null}
            width={900}
            zIndex={10000}
            closeIcon={<Close style={{ fontSize: 18 }} />}
        >
            <ModalContent>
                <Tabs
                    defaultActiveKey={selectedStateCode}
                    activeKey={selectedStateCode}
                    items={tabItems}
                    onChange={(key) => setActiveStateCode(key)}
                />
            </ModalContent>
        </Modal>
    );
};

export default StandardsModal;
